import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  uiCurrency,
  uiLocaleTag,
  uiTimeZone,
} from '@besiktning/ui'
import {
  ClipboardList,
  FileText,
  Calendar,
  TrendingUp,
  Star,
  Droplets,
  CheckCircle2,
  Circle,
  ChevronRight,
  X,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts'
import { useAuthStore } from '../store/authStore'
import { t } from '../i18n'
import { garageApi, carCareApi } from '@besiktning/ui'
import type {
  CarCareDashboardStats,
  DashboardActivityItem,
  GarageDashboardStats,
  Onboarding,
} from '@besiktning/ui'

function formatRelativeTimeSv(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const now = Date.now()
  const diffSec = Math.round((now - then) / 1000)
  if (diffSec < 45) return t('dashboard.justNow')
  const diffMin = Math.round(diffSec / 60)
  if (diffMin < 60) return t('dashboard.minutesAgo', { n: diffMin })
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return t('dashboard.hoursAgo', { n: diffHr })
  const diffDay = Math.round(diffHr / 24)
  if (diffDay < 7)
    return diffDay === 1
      ? t('dashboard.dayAgo', { n: diffDay })
      : t('dashboard.daysAgo', { n: diffDay })
  return new Date(iso).toLocaleDateString(uiLocaleTag(), {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: uiTimeZone(),
  })
}

function activityHref(item: DashboardActivityItem): string | null {
  switch (item.type) {
    case 'request':
      return item.entity_id ? `/requests/${item.entity_id}` : null
    case 'quote':
      return item.request_id ? `/requests/${item.request_id}` : null
    case 'booking':
    case 'car_care_booking':
      return item.entity_id ? `/jobs/${item.entity_id}` : null
    case 'review':
      return '/reviews'
    default:
      return null
  }
}

function activityIcon(type: DashboardActivityItem['type']) {
  switch (type) {
    case 'quote':
      return { Icon: FileText, bg: 'bg-green-100', color: 'text-green-600' }
    case 'booking':
      return { Icon: Calendar, bg: 'bg-purple-100', color: 'text-purple-600' }
    case 'car_care_booking':
      return { Icon: Droplets, bg: 'bg-cyan-100', color: 'text-cyan-600' }
    case 'review':
      return { Icon: Star, bg: 'bg-amber-100', color: 'text-amber-600' }
    default:
      return { Icon: ClipboardList, bg: 'bg-blue-100', color: 'text-blue-600' }
  }
}

const ONBOARDING_HREF: Record<string, string> = {
  description: '/profile',
  branding: '/profile',
  hours: '/profile',
  location: '/profile',
  phone: '/profile',
  services: '/services',
  staff: '/staff',
  first_quote: '/requests',
  first_booking: '/jobs',
}

// Per-browser "dölj". The card reopens on another device, which is fine for a
// nudge; onboarding_completed_at on the server is the fact that matters.
function readDismissed(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

function OnboardingCard({
  onboarding,
  storageKey,
}: {
  onboarding: Onboarding
  storageKey: string
}) {
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState(() => readDismissed(storageKey))

  const { steps } = onboarding
  const requiredLeft = steps.some((s) => s.required && !s.done)
  if (dismissed || !requiredLeft) return null

  const done = steps.filter((s) => s.done).length

  const dismiss = () => {
    try {
      localStorage.setItem(storageKey, '1')
    } catch {
      // Private mode: hide for this visit only.
    }
    setDismissed(true)
  }

  return (
    <Card className="mb-8">
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle>{t('dashboard.onboarding.title')}</CardTitle>
          <CardDescription>
            {t('dashboard.onboarding.progress', { done, total: steps.length })} ·{' '}
            {t('dashboard.onboarding.subtitle')}
          </CardDescription>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('dashboard.onboarding.dismiss')}
          className="p-1 text-gray-400 hover:text-gray-600 rounded"
        >
          <X className="h-4 w-4" />
        </button>
      </CardHeader>
      <CardContent>
        <div className="h-2 mb-4 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 transition-all"
            style={{ width: `${(done / steps.length) * 100}%` }}
          />
        </div>
        <ul className="divide-y divide-gray-100">
          {steps.map((step) => {
            const href = ONBOARDING_HREF[step.key]
            return (
              <li key={step.key}>
                <button
                  type="button"
                  disabled={step.done || !href}
                  onClick={() => href && navigate(href)}
                  className="w-full flex items-center gap-3 py-2.5 text-left enabled:hover:bg-gray-50 rounded"
                >
                  {step.done ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                  ) : (
                    <Circle className="h-5 w-5 text-gray-300 shrink-0" />
                  )}
                  <span
                    className={`flex-1 text-sm ${
                      step.done ? 'text-gray-400 line-through' : 'text-gray-900'
                    }`}
                  >
                    {t(`dashboard.onboarding.${step.key}`)}
                  </span>
                  {!step.required && !step.done && (
                    <span className="text-xs text-gray-400">
                      {t('dashboard.onboarding.optional')}
                    </span>
                  )}
                  {!step.done && <ChevronRight className="h-4 w-4 text-gray-400" />}
                </button>
              </li>
            )
          })}
        </ul>
      </CardContent>
    </Card>
  )
}

export default function Dashboard() {
  const navigate = useNavigate()
  const { user, currentMembership } = useAuthStore()

  const isCarCare = currentMembership?.type === 'car_care'

  const { data: stats, isLoading } = useQuery<
    GarageDashboardStats | CarCareDashboardStats,
    Error,
    GarageDashboardStats | CarCareDashboardStats,
    string[]
  >({
    queryKey: ['dashboard', isCarCare ? 'car_care' : 'garage'],
    queryFn: () =>
      isCarCare ? carCareApi.getDashboard() : garageApi.getDashboard(),
    // The onboarding card reflects edits made on Profile/Services; the global
    // 5-minute staleTime would show it stale right after the user comes back.
    refetchOnMount: 'always',
  })

  const statCards = useMemo(() => {
    if (isCarCare) {
      const s = stats as CarCareDashboardStats | undefined
      return [
        {
          title: t('dashboard.pendingBookings'),
          value: isLoading ? '...' : String(s?.pending_bookings ?? 0),
          icon: ClipboardList,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
        },
        {
          title: t('dashboard.totalBookings'),
          value: isLoading ? '...' : String(s?.total_bookings ?? 0),
          icon: FileText,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
        },
        {
          title: t('dashboard.upcomingBookings'),
          value: isLoading ? '...' : String(s?.upcoming_bookings ?? 0),
          icon: Calendar,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
        },
        {
          title: t('dashboard.averageRating'),
          value: isLoading
            ? '...'
            : s?.average_rating != null && s.average_rating > 0
              ? `${Number(s.average_rating).toFixed(1)} / 5`
              : '—',
          icon: Star,
          color: 'text-purple-600',
          bgColor: 'bg-purple-50',
        },
      ]
    }

    const s = stats as GarageDashboardStats | undefined
    return [
      {
        title: t('dashboard.newRequests'),
        value: isLoading ? '...' : String(s?.pending_requests ?? 0),
        icon: ClipboardList,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
      },
      {
        title: t('dashboard.activeQuotes'),
        value: isLoading ? '...' : String(s?.total_quotes ?? 0),
        icon: FileText,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
      },
      {
        title: t('dashboard.upcomingBookings'),
        value: isLoading ? '...' : String(s?.upcoming_bookings ?? 0),
        icon: Calendar,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
      },
      {
        title: t('dashboard.conversionRate'),
        value: isLoading ? '...' : `${s?.quote_conversion_rate ?? 0}%`,
        icon: TrendingUp,
        color: 'text-purple-600',
        bgColor: 'bg-purple-50',
      },
    ]
  }, [isCarCare, isLoading, stats])

  const revenueData = useMemo(() => {
    const rows = stats?.revenue_by_month
    if (!rows?.length) return []
    return rows.map((r) => ({ month: r.month, revenue: r.revenue }))
  }, [stats])

  const weeklyLineData = useMemo(() => {
    if (!stats) return []
    if (isCarCare) {
      const s = stats as CarCareDashboardStats
      return (s.bookings_this_week_by_day ?? []).map((d) => ({
        day: d.day,
        quotes: d.count,
      }))
    }
    const s = stats as GarageDashboardStats
    return s.quotes_this_week_by_day ?? []
  }, [stats, isCarCare])

  const recentActivity = useMemo(() => {
    const items = stats?.recent_activity
    return Array.isArray(items) ? items : []
  }, [stats])

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">
          {t('dashboard.welcomeBack', { name: user?.name || user?.email || '' })}
        </p>
      </div>

      {stats?.onboarding && currentMembership && (
        <OnboardingCard
          onboarding={stats.onboarding}
          storageKey={`onboarding-dismissed:${currentMembership.type}:${currentMembership.id}`}
        />
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((card) => (
          <Card key={card.title}>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${card.bgColor}`}>
                  <card.icon className={`h-6 w-6 ${card.color}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">{card.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.revenueTitle')}</CardTitle>
            <CardDescription>
              {isCarCare
                ? t('dashboard.revenueDescCarCare')
                : t('dashboard.revenueDescGarage')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex h-[250px] items-center justify-center text-sm text-gray-500">
                {t('dashboard.loading')}
              </div>
            ) : revenueData.length === 0 ? (
              <div className="flex h-[250px] items-center justify-center text-sm text-gray-500">
                {t('dashboard.noDataYet')}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number) =>
                      new Intl.NumberFormat(uiLocaleTag(), {
                        style: 'currency',
                        currency: uiCurrency(),
                        maximumFractionDigits: 0,
                      }).format(value)
                    }
                  />
                  <Bar dataKey="revenue" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {isCarCare
                ? t('dashboard.newBookingsThisWeek')
                : t('dashboard.quotesThisWeek')}
            </CardTitle>
            <CardDescription>
              {isCarCare
                ? t('dashboard.bookingsPerDayDesc')
                : t('dashboard.quotesPerDayDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex h-[250px] items-center justify-center text-sm text-gray-500">
                {t('dashboard.loading')}
              </div>
            ) : weeklyLineData.length === 0 ? (
              <div className="flex h-[250px] items-center justify-center text-sm text-gray-500">
                {t('dashboard.noDataYet')}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={weeklyLineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="quotes"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ fill: '#10b981' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.recentActivity')}</CardTitle>
          <CardDescription>
            {isCarCare
              ? t('dashboard.recentBookingUpdates')
              : t('dashboard.requestsQuotesBookings')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-gray-500">{t('dashboard.loading')}</p>
          ) : recentActivity.length === 0 ? (
            <p className="text-sm text-gray-500">{t('dashboard.noActivityYet')}</p>
          ) : (
            <div className="space-y-4">
              {recentActivity.map((item) => {
                const { Icon, bg, color } = activityIcon(item.type)
                const href = activityHref(item)
                return (
                  <div
                    key={item.id}
                    className={`flex items-center p-3 bg-gray-50 rounded-lg ${
                      href ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''
                    }`}
                    role={href ? 'link' : undefined}
                    tabIndex={href ? 0 : undefined}
                    onClick={href ? () => navigate(href) : undefined}
                    onKeyDown={
                      href
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') navigate(href)
                          }
                        : undefined
                    }
                  >
                    <div className={`p-2 rounded-full ${bg}`}>
                      <Icon className={`h-4 w-4 ${color}`} />
                    </div>
                    <div className="ml-3 flex-1 min-w-0">
                      <p className="text-sm font-medium">{item.title}</p>
                      <p className="text-xs text-gray-500 truncate">{item.description}</p>
                    </div>
                    <span className="text-xs text-gray-400 shrink-0 ml-2">
                      {formatRelativeTimeSv(item.created_at)}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
